// How RenderKeycapTexts draws a text legend, out of the component (backfill chain 11, Oct
// 2026): the key its font is cached and registered under, the fontkit axes of its style, its
// geometry over the loaded fontkit font (the box the legend is placed by, the transform that
// offsets and turns it, the text element's own x and y, and the highlight, underline and
// strikethrough around the text), the inline style the text element carries and the paints
// of its decoration lines. The order processing renders these into the printing SVG, whose
// text conversion (convertTextNodesToPaths, utils/exportHelpers.js) reads the x, the y and
// the style back; the customizer's downloads go the same way.
import { getGlyphDimensions, getLegendPosition } from './legendGeometry';

export const fontCacheKey = (fontFamily) => fontFamily.replace(/[[\],]/g, ''); // the style's font family without brackets and commas (NotoSans[wdth,wght] → NotoSanswdthwght): the key fetchFont (utils/fontHelpers.js) caches the fontkit font under and names the FontFace with, and the font-family the text's style names, as the brackets and commas would keep the browser from applying the font

export const textVariationSettings = (style) => {
  const variations = {};

  // Conditionally add each variation axis if defined
  if (style.fontWeight) variations.wght = style.fontWeight;
  if (style.fontStretch) variations.wdth = style.fontStretch;
  if (style.fontStyle) variations.slnt = style.fontStyle;

  return variations;
}; // the fontkit variation axes of a text's style: its weight, width and slant, each when the style holds one (the editor leaves an axis the font lacks null, and a slant of 0 is no slant)

export const textLegendLayout = (
  { text, position, customStyles, style, isActiveStates },
  font,
  printableAreas
) => {
  const {
    totalWidth,
    totalHeight,
    leftSpacing,
    rightSpacing,
    distanceFromCentralBaselineToTopOfVisualBoundingBox,
    typographicBoundingBoxHeight,
    topTypographicBoundingBoxToVisualTopOffset,
    distanceFromAlphabeticBaselineToTopOfTypographicBoundingBox,
    fontBoundingBoxHeight,
    distanceFromAlphabeticBaselineToTopOfVisualBoundingBox,
    distanceFromAlphabeticBaselineToTopOfFontBoundingBox,
    distanceFromCentralBaselineToTopOfTypographicBoundingBox,
    distanceFromCentralBaselineToTopOfFontBoundingBox,
    topFontBoundingBoxToVisualTopOffset,
    distanceFromTopOfTypographicBoundingBoxToTopOfFontBoundingBox,
    leftmost,
  } = getGlyphDimensions(
    font,
    text,
    style.fontSize,
    isActiveStates.textSpacing ? style.letterSpacing : null,
    isActiveStates.textSpacing ? style.wordSpacing : null,
    isActiveStates.stroke ? style.strokeWidth : null,
    textVariationSettings(style)
  ); // Calculate detailed glyph dimensions based on font settings and spacing adjustments

  let selectedBoundingBoxHeight;
  if (position.boundingBox === 'compact') {
    selectedBoundingBoxHeight = totalHeight;
  } else if (position.boundingBox === 'standard') {
    selectedBoundingBoxHeight = typographicBoundingBoxHeight;
  } else if (position.boundingBox === 'extended') {
    selectedBoundingBoxHeight = fontBoundingBoxHeight;
  } // Depending on which bbox type the user selected, use a different height for calculating the text position

  const { x, y } = getLegendPosition(
    position,
    totalWidth,
    selectedBoundingBoxHeight,
    printableAreas
  ); // Calculate the x and y coordinates to position the text within a grid cell

  const halfStroke = isActiveStates.stroke ? style.strokeWidth / 2 : 0; // the stroke extends the glyphs by half its width on every side
  const fullStroke = isActiveStates.stroke ? style.strokeWidth : 0;

  const transform = `translate(${position.xOffset}, ${position.yOffset}) rotate(${position.rotation}, ${x + totalWidth / 2}, ${y + selectedBoundingBoxHeight / 2})`; // the entry's offsets (mm) and its rotation around the box's centre. The editor writes all three into every entry (0 by default) and a predefined legend is merged over those defaults, so the guards for an entry without them went in chain 11 (user decision): a missing one would write "undefined" into the attribute now, which the browser ignores as a whole, offsets and rotation alike. Until chain 11 the two parts stood on lines of their own inside the attribute, which the SVG transform grammar reads the same

  const highlight = !isActiveStates.highlight
    ? null
    : // Conditionally render the text highlight if selected bbox is 'compact':
      position.boundingBox === 'compact' &&
        customStyles.highlight.mode === 'compact'
      ? { x, y, width: totalWidth, height: totalHeight }
      : position.boundingBox === 'compact' &&
          customStyles.highlight.mode === 'standard'
        ? {
            x: x - leftSpacing,
            y: y - topTypographicBoundingBoxToVisualTopOffset + halfStroke, // if the boundingBox is in 'compact' mode, the y position changes with the strokeWidth. The y position needs to be readjusted for the 'standard' highlight, as the typographic bbox (standard highlight) does not change with the strokeWidth and should stay at the same y position
            width: totalWidth + leftSpacing + rightSpacing,
            height: typographicBoundingBoxHeight,
          }
        : position.boundingBox === 'compact' &&
            customStyles.highlight.mode === 'extended'
          ? {
              x: x - leftSpacing,
              y: y - topFontBoundingBoxToVisualTopOffset + halfStroke, // if the boundingBox is in 'compact' mode, the y position changes with the strokeWidth. The y position needs to be readjusted for the 'extended' highlight, as the font bbox (extended highlight) does not change with the strokeWidth and should stay at the same y position
              width: totalWidth + leftSpacing + rightSpacing,
              height: fontBoundingBoxHeight,
            }
          : // Conditionally render the text highlight if selected bbox is 'standard':
            position.boundingBox === 'standard' &&
              customStyles.highlight.mode === 'compact'
            ? {
                x,
                y: y + topTypographicBoundingBoxToVisualTopOffset - halfStroke, // if the boundingBox is in 'standard' mode, the y position doesn't change with the strokeWidth. The y position needs to be readjusted for the 'compact' highlight, as the visual bbox (compact highlight) changes with the strokeWidth and should adjust it's y position
                width: totalWidth,
                height: totalHeight,
              }
            : position.boundingBox === 'standard' &&
                customStyles.highlight.mode === 'standard'
              ? {
                  x: x - leftSpacing,
                  y,
                  width: totalWidth + leftSpacing + rightSpacing,
                  height: typographicBoundingBoxHeight,
                }
              : position.boundingBox === 'standard' &&
                  customStyles.highlight.mode === 'extended'
                ? {
                    x: x - leftSpacing,
                    y:
                      y -
                      distanceFromTopOfTypographicBoundingBoxToTopOfFontBoundingBox,
                    width: totalWidth + leftSpacing + rightSpacing,
                    height: fontBoundingBoxHeight,
                  }
                : // Conditionally render the text highlight if selected bbox is 'extended':
                  position.boundingBox === 'extended' &&
                    customStyles.highlight.mode === 'compact'
                  ? {
                      x,
                      y: y + topFontBoundingBoxToVisualTopOffset - halfStroke, // if the boundingBox is in 'extended' mode, the y position doesn't change with the strokeWidth. The y position needs to be readjusted for the 'compact' highlight, as the visual bbox (compact highlight) changes with the strokeWidth and should adjust it's y position
                      width: totalWidth,
                      height: totalHeight,
                    }
                  : position.boundingBox === 'extended' &&
                      customStyles.highlight.mode === 'standard'
                    ? {
                        x: x - leftSpacing,
                        y:
                          y +
                          distanceFromTopOfTypographicBoundingBoxToTopOfFontBoundingBox,
                        width: totalWidth + leftSpacing + rightSpacing,
                        height: typographicBoundingBoxHeight,
                      }
                    : position.boundingBox === 'extended' &&
                        customStyles.highlight.mode === 'extended'
                      ? {
                          x: x - leftSpacing,
                          y,
                          width: totalWidth + leftSpacing + rightSpacing,
                          height: fontBoundingBoxHeight,
                        }
                      : null; // the highlight rect, in the highlight's own mode around a box of the entry's mode

  const textX =
    x -
    leftmost + // leftmost is either the left edge of the first glyph (= left spacing of the first glyph) or the furthest left point due to negative spacing (the value the text needs to be adjusted by, when negative letter and/or word spacing shifts the text to the left, away from the texts x=0 starting point)
    halfStroke; // the stroke width effects the x position of the text, as I use anchor="start" to position the text on the x axis

  const textY =
    position.boundingBox === 'compact'
      ? y + distanceFromAlphabeticBaselineToTopOfVisualBoundingBox + halfStroke // the stroke width must be added to the compact bbox, because it effects the height and of the rendered glyphs (but it doesn't change the typographic and font bboxes)
      : position.boundingBox === 'standard'
        ? y + distanceFromAlphabeticBaselineToTopOfTypographicBoundingBox
        : position.boundingBox === 'extended'
          ? y + distanceFromAlphabeticBaselineToTopOfFontBoundingBox
          : 0; // the stroke width does NOT effect the y position of the text, as the text is positioned based on the dominantBaseline of the text (the dominant baseline of the text does not change when the stroke width changes)

  const baselineBelowBoxTop =
    position.boundingBox === 'compact'
      ? distanceFromAlphabeticBaselineToTopOfVisualBoundingBox + fullStroke // as the text expands to the bottom and the visual bbox expands to the top (by the value of the strokeWidth / 2), the position of the underline has to be adjusted by both the changes in the bbox (expanding upwards) and the text (expanding downwards).
      : position.boundingBox === 'standard'
        ? distanceFromAlphabeticBaselineToTopOfTypographicBoundingBox +
          halfStroke // for 'standard' bbox mode the text stays at the same position relative to the bbox (also bbox doesn't change), but the text expands downwards by the value of (strokeWidth / 2). The underline should stay at the same y position relative to the text
        : position.boundingBox === 'extended'
          ? distanceFromAlphabeticBaselineToTopOfFontBoundingBox + halfStroke // for 'extended' bbox mode the text stays at the same position relative to the bbox (also bbox doesn't change), but the text expands downwards by the value of (strokeWidth / 2). The underline should stay at the same y position relative to the text
          : 0; // the underline hangs from here: the baseline below the box's top, half a stroke lower with the glyphs' bottom

  const centralBaselineBelowBoxTop =
    position.boundingBox === 'compact'
      ? distanceFromCentralBaselineToTopOfVisualBoundingBox + halfStroke // in 'compact' bbox mode the visual bbox expands, but the actual bbox of the text (typographic bbox, which is responisble for the texts y positioning) doesn't change with the strokeWidth. To have the strikethrough stick at the same y position relative to it's text, it has to be moved downwards by half the strokeWidth (to balance out the expanding visual bbox, which results in the y value moving upwards by half the stroke width)
      : position.boundingBox === 'standard'
        ? distanceFromCentralBaselineToTopOfTypographicBoundingBox
        : position.boundingBox === 'extended'
          ? distanceFromCentralBaselineToTopOfFontBoundingBox
          : 0; // the strikethrough runs here: the central baseline below the box's top

  const decorationLine = (decoration, lineY) => ({
    x1: x - leftSpacing - decoration.widthAdjustment / 2,
    y1: lineY,
    x2: x + rightSpacing + totalWidth + decoration.widthAdjustment / 2, // no leftSpacing included, because x already includes that
    y2: lineY,
  }); // an underline or strikethrough over the text's advance box, widened by its adjustment

  const underline = isActiveStates.underline
    ? decorationLine(
        customStyles.underline,
        y +
          baselineBelowBoxTop +
          (customStyles.underline.yOffset +
            customStyles.underline.thickness / 2)
      )
    : null;

  const strikethrough = isActiveStates.strikethrough
    ? decorationLine(
        customStyles.strikethrough,
        y + centralBaselineBelowBoxTop + customStyles.strikethrough.yOffset
      )
    : null;

  return {
    box: { x, y, width: totalWidth, height: selectedBoundingBoxHeight }, // the legend's box in the printable area: its mode's height (the glyphs with their stroke, the font's ascent and descent, or the font's whole box), placed in its grid cell by getLegendPosition; the editor draws it in green
    transform,
    text: { x: textX, y: textY }, // the text element's start and its alphabetic baseline
    highlight,
    underline,
    strikethrough,
  };
}; // the geometry of one text entry of a keycap over its loaded fontkit font and the keycap's printable rectangles, as the component draws it

export const textLegendStyle = (
  style,
  isActiveStates,
  dataComingFromLayoutEditor
) => ({
  ...style,
  fontFamily: fontCacheKey(style.fontFamily), // the FontFace's name (if the fontFamily name has brackets or commas in it, the font is not applied to the text)
  fill: isActiveStates.fill
    ? style.fill
    : dataComingFromLayoutEditor
      ? '#ffffff' // if the text is being rendered in the LayoutEditScreen, use white as the text color
      : '#000000',
  strokeWidth: isActiveStates.stroke ? style.strokeWidth : 0,
  stroke: isActiveStates.stroke ? style.stroke : 'none',
  letterSpacing: isActiveStates.textSpacing ? style.letterSpacing : 0,
  wordSpacing: isActiveStates.textSpacing ? style.wordSpacing : 0,
  fontStretch: style.fontStretch ? `${style.fontStretch}%` : undefined,
  fontStyle: style.fontStyle ? `oblique ${style.fontStyle}deg` : undefined,
  whiteSpace: 'pre', // added whiteSpace: 'pre', so that consecutive spaces in a <text> element are not treated as a single space
}); // the inline style of the text element: the entry's style with the inactive settings neutralised, the stretch as a percentage and the slant as an oblique angle, which the export's text conversion parses back into the font's axes

export const decorationLineStyle = (decoration) => ({
  stroke: decoration.color || 'black',
  strokeWidth: decoration.thickness || 1,
  strokeDasharray:
    decoration.lineStyle === 'solid'
      ? 'none' // For solid, no dash
      : decoration.lineStyle === 'dotted'
        ? `0 ${decoration.gapLength}` // Dotted: Dash length is always 0, gap length is dynamic
        : decoration.lineStyle === 'dashed'
          ? `${decoration.dashLength} ${decoration.gapLength}` // Dashed: Dynamic dash and gap lengths
          : 'none', // Default fallback
  strokeLinecap:
    decoration.lineStyle === 'dotted'
      ? 'round' // If line style is dotted, use 'round'
      : decoration.lineCap || 'butt', // Otherwise, use lineCap or default to 'butt'
}); // the paint of an underline or strikethrough (customStyles.underline or .strikethrough): its colour (black for an empty value) and thickness (a 0 becomes 1: `KNOWN_BUGS.md`, a thickness of 0), and its dash pattern and line cap by its line style
